import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Wallet, TrendingUp, TrendingDown, ArrowDownToLine, ArrowUpFromLine, RotateCcw, History } from 'lucide-react'
import {
  usePersonalBankroll,
  useSetupPersonalBankroll,
  usePersonalBankrollHistory,
  useDepositPersonalBankroll,
  useWithdrawPersonalBankroll,
  useResetPersonalBankroll,
} from '@/hooks/usePersonalBankroll'
import { SkeletonList } from '@/components/LoadingSkeleton'
import { EvolutionChart } from '@/components/EvolutionChart'
import { BankrollMovementModal } from '@/components/BankrollMovementModal'
import { ResetBankrollModal } from '@/components/ResetBankrollModal'
import { cn } from '@/lib/utils'

const MOVEMENT_LABEL: Record<string, { label: string; sign: '+' | '-' | '' }> = {
  INITIAL_BALANCE: { label: 'Banca inicial', sign: '' },
  DEPOSIT: { label: 'Depósito', sign: '+' },
  WITHDRAWAL: { label: 'Retiro', sign: '-' },
  BET_STAKE: { label: 'Apuesta', sign: '-' },
  BET_WIN: { label: 'Ganancia', sign: '+' },
  BET_LOSS: { label: 'Pérdida', sign: '-' },
  BET_PUSH: { label: 'Push', sign: '' },
  QUINIELA_STAKE: { label: 'La Quiniela', sign: '-' },
  QUINIELA_PRIZE: { label: 'Premio La Quiniela', sign: '+' },
  ADJUSTMENT: { label: 'Ajuste', sign: '' },
  RESET: { label: 'Reinicio', sign: '' },
}

function formatMoney(value: number | undefined | null): string {
  if (value == null) return 'N/D'
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value)
}

/** Fase R.11 (Bloque F-M, 17/08/2026) + P0.23 (22/08/2026, Secciones 15/16/32): "Mi Banca
 * Personal" -- contabilidad NUEVA e independiente de la banca administrativa histórica del
 * sistema (P0.53, BLOCK A: esa cuenta ya no vive en Client Web, ver Dashboard). Beneficio/
 * Pérdida y ROI vienen EXCLUSIVAMENTE del ledger
 * de apuestas liquidadas (`betting_profit_loss`); capital aportado/retirado se muestra por
 * separado (`net_contributions`/`total_deposits`/`total_withdrawals`) para que un depósito
 * nunca se confunda con una ganancia deportiva (validación exacta de la Sección 32). */
export function MyBankrollPage() {
  const { data: bankroll, isLoading } = usePersonalBankroll()
  const [amount, setAmount] = useState('')
  const setup = useSetupPersonalBankroll()
  const { data: history } = usePersonalBankrollHistory()

  const [modal, setModal] = useState<'deposit' | 'withdraw' | 'reset' | null>(null)
  const deposit = useDepositPersonalBankroll()
  const withdraw = useWithdrawPersonalBankroll()
  const reset = useResetPersonalBankroll()

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <h1 className="text-lg font-bold">Mi Banca</h1>
        <SkeletonList count={2} />
      </div>
    )
  }

  if (!bankroll?.configured) {
    const parsed = Number(amount.replace(',', '.'))
    const canSubmit = amount.trim().length > 0 && parsed > 0 && !setup.isPending

    // P0.56 (Sección 11 del encargo): estado vacío premium -- explica qué falta y qué hace
    // el usuario, nunca una caja con campos "N/D" sueltos.
    return (
      <div className="p-4 space-y-5 md:max-w-lg md:mx-auto">
        <h1 className="font-display text-lg font-semibold text-text">Mi Banca</h1>
        <div className="card p-6 rounded-2xl space-y-5">
          <div className="text-center space-y-2">
            <Wallet className="h-7 w-7 mx-auto text-brand" />
            <p className="text-base font-bold text-text">Empieza a medir tu rendimiento real</p>
          </div>

          <ol className="space-y-3">
            <li className="flex gap-3">
              <span className="figure h-5 w-5 rounded-full bg-brand-soft text-brand text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
              <p className="text-xs text-text-muted leading-relaxed">Define tu banca inicial -- solo un número de referencia, nunca mueve dinero real.</p>
            </li>
            <li className="flex gap-3">
              <span className="figure h-5 w-5 rounded-full bg-brand-soft text-brand text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
              <p className="text-xs text-text-muted leading-relaxed">Marca <strong className="text-text">LA JUGUÉ</strong> en cada pick que realmente apostaste, con tu cuota real.</p>
            </li>
            <li className="flex gap-3">
              <span className="figure h-5 w-5 rounded-full bg-brand-soft text-brand text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
              <p className="text-xs text-text-muted leading-relaxed">John Anthony calcula tu evolución real -- P/L, ROI, win rate, cuota media.</p>
            </li>
          </ol>

          <div className="hairline pt-4 space-y-2">
            <label className="text-[10px] font-bold uppercase text-text-dim">Capital inicial</label>
            <input
              type="number" inputMode="decimal" min={1} step="0.01" placeholder="0.00" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-panel-elevated border border-border/60 rounded-xl px-3 py-2.5 text-sm font-mono font-bold text-text text-center focus:outline-none focus:border-brand/50"
            />
          </div>
          <button
            disabled={!canSubmit}
            onClick={() => setup.mutate(parsed)}
            className="w-full py-2.5 rounded-xl bg-brand text-black text-xs font-black uppercase tracking-wider disabled:opacity-40 disabled:pointer-events-none"
          >
            Configurar mi banca
          </button>
          {setup.isError && <p className="text-[10px] text-danger text-center">No se pudo crear la banca. Intenta de nuevo.</p>}
        </div>
      </div>
    )
  }

  const bettingProfitPositive = (bankroll.betting_profit_loss ?? 0) >= 0
  const evolutionPoints = (history ?? [])
    .slice()
    .reverse()
    .map((t) => t.balance_after)

  return (
    <div className="p-4 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Mi Banca</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setModal('deposit')}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-brand text-black text-[10px] font-black uppercase tracking-wider active-scale"
          >
            <ArrowDownToLine className="h-3 w-3" /> Depositar
          </button>
          <button
            onClick={() => setModal('withdraw')}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-panel-elevated border border-border text-text text-[10px] font-black uppercase tracking-wider active-scale"
          >
            <ArrowUpFromLine className="h-3 w-3" /> Retirar
          </button>
        </div>
      </div>

      <div className="card p-5 rounded-2xl space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/30 text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">Saldo actual</p>
            <p className="text-base font-mono font-black text-brand">{formatMoney(bankroll.current_balance)}</p>
          </div>
          <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/30 text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">P/L Deportivo</p>
            <p className={cn('text-base font-mono font-black flex items-center justify-center gap-1', bettingProfitPositive ? 'text-success' : 'text-danger')}>
              {bettingProfitPositive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
              {formatMoney(bankroll.betting_profit_loss)}
            </p>
          </div>
          <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/30 text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">ROI</p>
            <p className="text-base font-mono font-black text-text">{bankroll.roi != null ? `${bankroll.roi.toFixed(2)}%` : 'N/D'}</p>
          </div>
          <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/30 text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">Capital aportado</p>
            <p className="text-base font-mono font-black text-text">{formatMoney(bankroll.net_contributions)}</p>
          </div>
        </div>

        {/* Sección 32: verificación visual explícita -- depósito/retiro nunca alteran P/L Deportivo/ROI. */}
        <p className="text-[9px] text-text-dim text-center leading-relaxed px-2">
          El P/L Deportivo y el ROI se calculan SOLO con tus apuestas liquidadas -- depositar o retirar nunca los modifica.
        </p>

        <div className="grid grid-cols-3 gap-2 pt-1 border-t border-border/30">
          <div className="text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">Win Rate</p>
            <p className="text-sm font-mono font-black text-text">{bankroll.win_rate != null ? `${bankroll.win_rate.toFixed(1)}%` : 'N/D'}</p>
          </div>
          <div className="text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">Apostado total</p>
            <p className="text-sm font-mono font-black text-text">{formatMoney(bankroll.total_staked)}</p>
          </div>
          <div className="text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">Cuota media</p>
            <p className="text-sm font-mono font-black text-text">{bankroll.average_odds_played != null ? `@${bankroll.average_odds_played.toFixed(2)}` : 'N/D'}</p>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 pt-1 text-[10px] font-bold text-text-dim">
          <span className="text-success">{bankroll.won ?? 0} ganadas</span>
          <span className="text-danger">{bankroll.lost ?? 0} perdidas</span>
          <span>{bankroll.void ?? 0} void</span>
          <span>{bankroll.pending ?? 0} pendientes</span>
        </div>
      </div>

      {/* Evolución -- Sección 15 */}
      <section className="space-y-2">
        <h2 className="text-xs font-black uppercase tracking-wider text-text-dim">Evolución</h2>
        <div className="card p-4 rounded-2xl">
          <EvolutionChart points={evolutionPoints} />
        </div>
      </section>

      {/* Movimientos -- Sección 15/17 */}
      <section className="space-y-2">
        <h2 className="text-xs font-black uppercase tracking-wider text-text-dim">Movimientos</h2>
        {(!history || history.length === 0) ? (
          <p className="text-[11px] text-text-dim italic px-1">Sin movimientos todavía.</p>
        ) : (
          <div className="card p-0 divide-y divide-border/60 overflow-hidden">
            {history.slice(0, 15).map((t) => {
              const info = MOVEMENT_LABEL[t.transaction_type] ?? { label: t.transaction_type, sign: '' }
              const isNegative = t.amount < 0 || info.sign === '-'
              return (
                <div key={t.id} className="p-3 text-xs flex items-center justify-between gap-2">
                  <div>
                    <p className="font-bold text-text">{info.label}</p>
                    <p className="text-[10px] text-text-dim">{new Date(t.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <span className={cn('font-mono font-black', isNegative ? 'text-danger' : 'text-success')}>
                    {t.amount >= 0 ? '+' : ''}{formatMoney(t.amount)}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </section>

      <button
        onClick={() => setModal('reset')}
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-border/60 text-text-dim hover:text-warn hover:border-warn/40 text-[10px] font-black uppercase tracking-wider transition"
      >
        <RotateCcw className="h-3.5 w-3.5" /> Reiniciar Mi Banca
      </button>

      {/* P0.53 (29/08/2026, Sección 11 del encargo): "Mis Apuestas" se unificó dentro de
          "Historial" (junto a las recomendaciones oficiales, en pestañas separadas) -- ya no
          vive duplicada aquí. */}
      <Link
        to="/mas/historial?tab=apuestas"
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-panel-elevated border border-border/60 text-text hover:border-brand/40 hover:text-brand text-[10px] font-black uppercase tracking-wider transition"
      >
        <History className="h-3.5 w-3.5" /> Ver mis apuestas
      </Link>

      {(modal === 'deposit' || modal === 'withdraw') && (
        <BankrollMovementModal
          mode={modal}
          onClose={() => setModal(null)}
          currentBalance={bankroll.current_balance}
          isPending={modal === 'deposit' ? deposit.isPending : withdraw.isPending}
          isError={modal === 'deposit' ? deposit.isError : withdraw.isError}
          isSuccess={modal === 'deposit' ? deposit.isSuccess : withdraw.isSuccess}
          explainer="Esto registra dinero que añades o retiras de tu banca de seguimiento dentro de John Anthony. No mueve dinero bancario real ni afecta ninguna cuenta externa."
          onSubmit={(value) => (modal === 'deposit' ? deposit.mutate({ amount: value }) : withdraw.mutate({ amount: value }))}
        />
      )}

      {modal === 'reset' && (
        <ResetBankrollModal
          onClose={() => setModal(null)}
          isPending={reset.isPending}
          isError={reset.isError}
          entityLabel="Mi Banca"
          onConfirm={(reason) => {
            reset.mutate(reason || undefined, { onSuccess: () => setModal(null) })
          }}
        />
      )}
    </div>
  )
}
